/*
 * argile-cart-recommendations.js — Colonne cross-sell du tiroir panier.
 *
 * Élément <argile-cart-recos> placé dans la colonne gauche du drawer (cf.
 * snippets/header-actions.liquid). À l'ouverture du drawer (détectée via
 * IntersectionObserver — un <dialog> fermé est display:none, donc non visible),
 * on récupère les recommandations COMPLÉMENTAIRES Shopify du dernier article du
 * panier (endpoint JSON `/recommendations/products.json?intent=complementary`),
 * on exclut ce qui est déjà au panier, et on bâtit les cartes côté client
 * (même parti pris DS que argile-recently-viewed.js — aucune Liquid à fetch).
 *
 * « AJOUTER AU PANIER » ajoute la variante par défaut puis émet `cart:update`
 * (bubble) : le <cart-items-component> re-fetch sa section → le panier se
 * rafraîchit, et la colonne cross-sell se recharge (le produit ajouté en sort).
 *
 * Si panier vide ou aucune reco → la colonne se masque (classe sur l'aside).
 */
(() => {
  'use strict';

  const escapeHtml = (str) =>
    String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  // Redimensionne une URL d'image CDN Shopify (ajoute &width=…).
  const sizedImage = (url, width) => {
    if (!url) return '';
    let u = url.startsWith('//') ? 'https:' + url : url;
    u += (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    return u;
  };

  const cartAddUrl = () =>
    (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add';

  // Échantillons (testeurs, nuanciers A4) : jamais proposés ici. Leur variante n'est
  // qu'un code de teinte, et ajoutée d'ici elle n'emporterait pas le nom de la teinte
  // que portent les ajouts depuis la fiche teinte et les pages projet
  // (cf. docs/plans/2026-10-21-001-feat-panier-echantillons-nom-teinte-plan.md, U4).
  const PREFIXES_ECHANTILLON = ['testeur-', 'nuancier-a4-'];
  const estEchantillon = (product) =>
    PREFIXES_ECHANTILLON.some((prefixe) => String(product.handle || '').startsWith(prefixe));

  // Variante à ajouter : 1re disponible, sinon la 1re tout court.
  const defaultVariant = (product) => {
    const variants = product.variants || [];
    return variants.find((v) => v.available) || variants[0] || null;
  };

  class ArgileCartRecos extends HTMLElement {
    constructor() {
      super();
      this.loaded = false;
      this.lastBasis = null; // product_id ayant servi de base à la dernière reco
      this._onCartUpdate = this._onCartUpdate.bind(this);
    }

    connectedCallback() {
      this.aside = this.closest('.cart-drawer__cross-sell');

      // Préchargement à l'inactivité : les recos sont prêtes AVANT l'ouverture
      // du drawer → pas de flicker large↔étroit (le drawer s'ouvre directement
      // à la bonne largeur selon qu'il y a des recos ou non).
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
      this._idle = idle(() => this.load());

      // Une maj panier (ajout/suppression/quantité) peut changer les recos.
      document.addEventListener('cart:update', this._onCartUpdate);
    }

    disconnectedCallback() {
      if (this._idle && window.cancelIdleCallback) window.cancelIdleCallback(this._idle);
      document.removeEventListener('cart:update', this._onCartUpdate);
    }

    _onCartUpdate() {
      this.loaded = false;
      this.load();
    }

    // Affiche la colonne (et élargit le drawer) uniquement quand des recos
    // existent ; sinon le drawer reste mono-colonne, sans flicker.
    setReady(ready) {
      if (this.aside) this.aside.classList.toggle('is-ready', ready);
      // Signale la fin du (re)chargement des recos (succès, vide ou erreur) : la
      // fiche produit attend cet event pour n'ouvrir le tiroir qu'une fois le
      // panier ET le cross-sell prêts (pas de flicker « items sans cross-sell »).
      document.dispatchEvent(new CustomEvent('argile:recos-ready', { bubbles: true, detail: { ready: ready } }));
    }

    async load() {
      if (this.loaded) return;
      this.loaded = true;

      try {
        const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        if (!cart || !cart.items || cart.items.length === 0) {
          this.setReady(false);
          return;
        }

        // Base de reco = dernier article ajouté (le plus pertinent).
        const basis = cart.items[cart.items.length - 1];
        const basisId = basis.product_id;
        const inCart = new Set(cart.items.map((i) => i.product_id));

        const limit = parseInt(this.dataset.limit, 10) || 4;
        const intent = this.dataset.intent || 'complementary';

        // Intent voulu d'abord (ex. complementary). S'il n'est pas configuré
        // dans l'admin (→ 0 produit), on retombe sur `related` (reco auto
        // Shopify) pour que la colonne s'affiche quand même.
        let products = await this.fetchRecos(basisId, intent, limit, inCart);
        if (!products.length && intent !== 'related') {
          products = await this.fetchRecos(basisId, 'related', limit, inCart);
        }

        if (!products.length) {
          this.setReady(false);
          return;
        }

        this.render(products);
        this.lastBasis = basisId;
        this.setReady(true);
      } catch (e) {
        console.error('[argile-xsell]', e);
        this.setReady(false);
      }
    }

    // Récupère les recos pour un intent donné, exclut ce qui est au panier.
    // On demande le plafond de l'endpoint (10) : sur un panier d'échantillons, Shopify
    // recommande surtout d'autres testeurs / A4, que `estEchantillon` écarte — avec
    // `limit + inCart.size` la colonne tombait à 2 cartes (mesuré le 21/10/2026).
    async fetchRecos(productId, intent, limit, inCart) {
      const base = (this.dataset.url || '/recommendations/products') + '.json';
      const url =
        base + '?product_id=' + productId + '&limit=' + Math.max(10, limit + inCart.size) + '&intent=' + intent;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('recommendations ' + res.status);
      const data = await res.json();
      return (data.products || [])
        .filter((p) => !inCart.has(p.id) && !estEchantillon(p))
        .slice(0, limit);
    }

    render(products) {
      const heading = this.dataset.heading || '';
      const cards = products.map((p) => this.cardHtml(p)).join('');
      this.innerHTML =
        (heading ? '<h2 class="argile-xsell__heading">' + escapeHtml(heading) + '</h2>' : '') +
        '<div class="argile-xsell__list">' + cards + '</div>';
    }

    cardHtml(product) {
      const variant = defaultVariant(product);
      const vid = variant ? variant.id : '';
      const sku = (variant && variant.sku) || '';
      // « Format » : titre de la variante si ce n'est pas la variante par défaut.
      const format = variant && variant.title && variant.title !== 'Default Title' ? variant.title : '';
      const img = sizedImage(product.featured_image || (product.images && product.images[0]) || '', 400);
      const canAdd = variant && variant.available !== false;
      const addLabel = this.dataset.addLabel || 'Ajouter au panier';
      const soldOutLabel = this.dataset.soldOutLabel || 'Épuisé';

      return (
        '<article class="argile-xsell-card">' +
        '<a class="argile-xsell-card__media" href="' + escapeHtml(product.url || '#') + '">' +
        (img
          ? '<img class="argile-xsell-card__img" src="' + escapeHtml(img) + '" alt="' + escapeHtml(product.title) + '" loading="lazy">'
          : '') +
        '</a>' +
        '<div class="argile-xsell-card__body">' +
        '<a class="argile-xsell-card__title" href="' + escapeHtml(product.url || '#') + '">' + escapeHtml(product.title) + '</a>' +
        (sku ? '<p class="argile-xsell-card__sku">' + escapeHtml(sku) + '</p>' : '') +
        (format ? '<p class="argile-xsell-card__format">' + escapeHtml(format) + '</p>' : '') +
        (canAdd
          ? '<button type="button" class="argile-xsell-card__add" data-xsell-add data-variant-id="' + escapeHtml(vid) + '">' +
            '<span class="argile-xsell-card__add-label">' + escapeHtml(addLabel) + '</span>' +
            '<span class="argile-xsell-card__add-arrow" aria-hidden="true">›</span>' +
            '</button>'
          : '<p class="argile-xsell-card__sold-out">' + escapeHtml(soldOutLabel) + '</p>') +
        '</div>' +
        '</article>'
      );
    }
  }

  if (!customElements.get('argile-cart-recos')) {
    customElements.define('argile-cart-recos', ArgileCartRecos);
  }

  // « Ajouter au panier » (délégation) — ajoute la variante par défaut puis
  // rafraîchit le panier Horizon via `cart:update` (re-fetch de la section).
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest && event.target.closest('[data-xsell-add]');
    if (!btn) return;
    event.preventDefault();

    const vid = btn.getAttribute('data-variant-id');
    if (!vid || btn.dataset.loading) return;
    btn.dataset.loading = '1';
    btn.classList.add('is-loading');

    try {
      await fetch(cartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: vid, quantity: 1 }),
      });
      const cart = await (await fetch('/cart.js')).json();
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data: { itemCount: cart.item_count, source: 'argile-xsell' } },
        })
      );
      btn.classList.add('is-added');
      setTimeout(() => btn.classList.remove('is-added'), 1200);
    } catch (e) {
      console.error('[argile-xsell] add to cart failed', e);
    } finally {
      delete btn.dataset.loading;
      btn.classList.remove('is-loading');
    }
  });

  /*
   * Filet de sécurité : compteur « Panier N » du heading du drawer.
   * Le heading vit dans la SECTION header, re-rendue par Horizon via la Section
   * Rendering API à chaque maj panier. Sur un thème de preview (non publié), ce
   * rendu peut renvoyer une section vide → `morphSection` échoue → le compteur du
   * drawer ne se met pas à jour (la navbar, elle, est mise à jour via l'event).
   * On met donc à jour le compteur du drawer côté client, depuis le nombre
   * d'articles porté par l'event `cart:update` (fallback : /cart.js).
   */
  function updateDrawerCount(count) {
    if (count == null || isNaN(count)) return;
    document.querySelectorAll('.cart-drawer__heading .cart-bubble').forEach(function (bubble) {
      bubble.classList.toggle('visually-hidden', count <= 0);
      var countEl = bubble.querySelector('.cart-bubble__text-count');
      if (countEl) {
        countEl.textContent = count > 99 ? '99+' : String(count);
        countEl.classList.toggle('hidden', count <= 0);
      }
    });
  }

  // Compteur du heading = somme des quantités des lignes VISIBLES du drawer.
  // C'est exactement ce que l'utilisateur voit, donc toujours cohérent — et ça
  // évite toute lecture serveur (le commit du +/- natif est débouncé → /cart.js
  // renvoie un compte obsolète juste après, d'où le retard d'un pas). Les inputs
  // sont mis à jour de façon optimiste au clic. `n === 0` (drawer fermé) → on ne
  // touche à rien.
  function syncDrawerCount() {
    var inputs = document.querySelectorAll('.cart-drawer__items .cart-items__quantity input');
    if (!inputs.length) return;
    var sum = 0;
    inputs.forEach(function (i) { sum += parseInt(i.value, 10) || 0; });
    updateDrawerCount(sum);
  }

  // +/- (et saisie) du panier : recalcul juste après la maj optimiste de l'input.
  document.addEventListener('click', function (event) {
    if (!event.target.closest || !event.target.closest('.cart-drawer__items .quantity-selector')) return;
    setTimeout(syncDrawerCount, 40);
    setTimeout(syncDrawerCount, 250);
  });
  // Ajout (cross-sell/fiche) ou suppression de ligne → recalcul après le re-render.
  document.addEventListener('cart:update', function () {
    setTimeout(syncDrawerCount, 60);
    setTimeout(syncDrawerCount, 450);
  });
})();
